// i18n/zh.ts
export const zh = {
  theme: {
    label: '配色主题',
    operator: '操盘紫',
    midnight: '午夜蓝',
    ember: '余烬橙',
  },
  app: {
    title: 'V12 量化交易系统',
    reconnect: '重连',
    lastUpdate: '更新时间',
  },
  nav: {
    dashboard: '仪表盘',
    'dashboard-v12': 'V12 仪表盘',
    tradier: 'Tradier券商',
    'realtime-scoring': '实时评分',
    broker: '券商',
    'paper-audit': '模拟审计',
    audit: 'R10 审计',
    admin: '管理',
    settings: '设置',
    databases: '数据库',
    'auto-trading': '自动交易',
    logout: '退出系统',
    logoutConfirm: '确认退出仪表盘登录?',
  },
  auditHistory: {
    title: '审计历史',
    subtitle: 'R10 实盘执行决策日志 · PostgreSQL / Kafka',
    refresh: '刷新',
    autoRefresh: '自动刷新 (15s)',
    recordsPrefix: '共 ',
    recordsSuffix: ' 条记录',
    buyScores: '买入评分',
    sellScores: '卖出评分',
    priceRecords: '价格记录',
    scoreBackfills: '评分回填',
    symbolPlaceholder: '标的代码 (如 AAPL)',
    allDecisions: '全部决策',
    clearFilters: '清除筛选',
    loadFailed: '加载失败',
    loading: '加载中...',
    columns: {
      time: '时间',
      symbol: '标的',
      side: '方向',
      decision: '决策',
      stage: '阶段',
      scores: '买/卖评分',
      priceChange: '价格/涨跌',
      message: '消息',
      orderId: '订单号',
    },
    noMatches: '没有匹配的记录',
    noRecords: '暂无审计记录',
    sides: { buy: '买入', sell: '卖出' },
    stages: {
      risk_gate: '风控门',
      plan: '计划',
      broker: '券商',
      signal: '信号',
    },
    scoreLabels: { buy: '买入', sell: '卖出', score: '评分' },
    priceLabels: { limit: '限', cost: '成本', peak: '峰值' },
    backfilledBy: (source: string) => `由 ${source} 回填`,
    showing: (from: number, to: number, total: number) => `显示 ${from}–${to} / ${total}`,
    previous: '上一页',
    next: '下一页',
    source: '数据来源',
    sourceDetail: 'Kafka 审计投影 · 每15秒自动刷新 · 最新数据显示在顶部 · EMITTED 行会按 intent_id 回填触发评分',
  },
  scores: {
    title: '评分排行',
  },
  realtime: {
    // 实时评分页 + LiveScoresV2Panel (LiveScoresV2Panel.tsx, RealtimeScoring.tsx).
    pageTitle: '实时评分',
    pageSubtitle: '买入排行 · 卖出排行 — 单一写入者 v13 SSOT',
    ownershipTag: 'topn:live_scores · 单一写入者 scoring_v13',
    panelTitle: (n: number) => `前 ${n} · 实时`,
    updatedAt: (time: string) => `更新于 ${time}`,
    sseLive: 'SSE 实时',
    loading: '加载中…',
    loadingVector: '正在加载向量…',
    errorPrefix: '错误',
    noData: '暂无数据',
    leaderboard: {
      symbol: '标的',
      session: '时段',
      live: '实时分',
      bar: '强度条',
      gap: '跳空',
      microprice: '微价格',
    },
    heatmap: {
      title: '15 字段信号向量',
      emptyHint: '点击排行榜行查看明细',
      composite: '综合分',
      groups: {
        quote: '报价',
        trade: '成交',
        summary: '汇总',
        timesale: '逐笔',
        session: '时段',
      },
      dimLabels: {
        spread_bps: '价差 bps',
        depth_imbalance: '深度失衡',
        microprice: '微价格',
        nbbo_staleness_score: 'NBBO 新鲜度',
        nbbo_at_print_bps: '成交 NBBO',
        flow_delta: '资金流 Δ',
        vol_burst: '量能爆发',
        vol_rate_session: '量比',
        turnover_rate_score: '换手',
        trade_intensity: '成交强度',
        range_pos: '区间位置',
        gap_open_score: '开盘跳空',
        cancel_rate: '撤单率',
        tape_quality: 'Tape 质量',
      },
    },
    decisions: {
      title: '实时决策',
      subtitle: (limit: number) => `最近 ${limit} 条`,
      empty: '暂无决策',
    },
    metrics: {
      title: '浸泡指标',
      tick: (ms: number) => `节拍 ${ms}ms`,
      invariantViolation: '不变量违规',
      brokerSubmitViolation: (count: number) => `broker_submit_count = ${count} (dry-run 必须为 0)`,
      dimCountViolation: (count: number) => `v14_signal_dim_count = ${count} (期望 15)`,
      sections: {
        orch: '编排器',
        autoTrader: '自动交易',
        tradierWs: 'Tradier WS',
      },
      labels: {
        symbol_count: '标的数量',
        v14_signal_dim_count: 'V14 信号维度数',
        decision_count: '决策数量',
        broker_submit_count: '券商提交数',
        ws_reconnect_count: 'WS 重连数',
      },
      symbolsActive: (count: number) => `${count} 个标的在编排器中活跃`,
      orchestratorIdle: '编排器空闲',
    },
    lifecycle: {
      title: '侧链生命周期',
      loading: '生命周期: 加载中…',
      errorPrefix: '生命周期',
      frozenMirror: '冻结镜像',
      frozen: '已冻结',
      live: '运行中',
      fsmPrefix: '状态机',
      fsm: '状态机',
      regime: '市场状态',
      freeze: '冻结',
    },
  },
  gate: {
    pass: '通过',
    wait: '等待',
    lock: '锁定',
  },
  market: {
    title: '行情塔',
    symbols: '交易标的',
    movers: '涨跌幅',
    watchlist: '自选',
    watchlistHint: 'Ranking Universe — 不是用户自选股 (user watchlist 在「券商 → 自选列表」)',
    topMovers: '活跃股',
    breadthUp: '上涨',
    breadthDown: '下跌',
    breadthFlat: '平盘',
  },
  broker: {
    title: '账户',
    equity: '总权益',
    unrealizedPnl: '未实现盈亏',
    brokers: '券商',
    connected: '已连接',
    disconnected: '已断开',
    positions: '持仓',
    orders: '订单',
    qty: '数量',
    costBasis: '成本',
    stopLoss: '止损',
    marketValue: '市值',
    unrealizedPnlShort: '浮动盈亏',
    // 2026-06-21: pane headers in the broker page (frontend/src/pages/Broker.tsx
    // lines 427 and 435). The English form is a market-segment name
    // ("Production / Live" = 券商真实账户, "Sandbox / Paper" = 模拟账户);
    // the Chinese form keeps that as a market-segment label rather than
    // a literal translation, because the page already uses 模拟 / 真实 for
    // the headline. See en.ts for the parallel entry.
    paneLive: 'Production / Live',
    panePaper: 'Sandbox / Paper',
  },
  signals: {
    title: '信号',
    top: 'Top 信号',
    buy: '买入信号',
    sell: '卖出信号',
  },
  execution: {
    title: '执行',
    fillRate: '成交率',
    totalOrders: '总订单',
    filled: '已成交',
    rejected: '已拒绝',
    activeOrders: '进行中订单',
  },
  metrics: {
    title: '系统指标',
    eventsPerSec: '事件/秒',
    quotes: '行情数',
    orders: '订单数',
    quoteDelay: '行情延迟',
    fillRate: '成交率',
    rejects: '拒绝数',
  },
  alerts: {
    title: '告警',
    all: '全部',
    critical: '严重',
    warning: '警告',
    noActive: '无活跃告警',
  },
  admin: {
    title: '管理',
    refresh: '刷新',
    system: '系统',
    adapters: '适配器',
    services: '服务',
    projections: '投影',
    running: '运行中',
    stopped: '已停止',
    connected: '已连接',
    disconnected: '已断开',
  },
  settings: {
    language: '语言',
    websocket: 'WebSocket',
    eventLog: '事件日志',
    checkpoints: '检查点',
    totalEvents: '总事件数',
    lastSeq: '最后序列',
    noCheckpoints: '无检查点',
    restore: '恢复',
    loading: '加载中...',
    save: '保存',
    saved: '已保存',
    saving: '保存中...',
    failed: '失败',
    show: '显示',
    hide: '隐藏',
    hotReloadNote: '保存后设置将自动重载',
    riskParams: '风险参数',
    maxSinglePosition: '单笔最大仓位',
    maxTotalExposure: '最大总敞口',
    maxOrdersPerDay: '每日最大订单数',
    maxHoldTime: '最长持仓时间',
    stopLoss: '止损',
    takeProfit: '止盈',
    circuitBreakerDrawdown: '熔断回撤',
    dailyDrawdownKill: '日内回撤熔断',
    orderSize: '订单规模',
    orderSizeOfPortfolio: '占组合百分比',
    execution: '执行',
    paperMode: '模拟账户',
    paperModeDesc: '模拟交易, 不产生真实订单',
    liveTradingEnabled: '启用真实交易',
    liveTradingEnabledDesc: '真实资金订单 — 请确认券商已连接',
    tradier: 'Tradier',
    tradierAccount: 'Tradier 账户',
    tradierApiKey: 'API Key',
    tradierSandboxApiKey: '沙盒 API Key',
    tradierPaperAccount: '模拟账户',
    tradierBaseUrl: '基础 URL',
    // 2026-06-22 build chain repair commit #3 — credential-specific
    // button states (distinct from generic settings.save/saved above).
    credSaved: '凭证已保存',
    credSave: '保存凭证',
    credSaving: '正在保存凭证…',
    credShow: '显示',
    credHide: '隐藏',
    configured: '已配置',
    telegramTitle: 'Telegram 告警凭证',
    telegramBotToken: 'Bot Token',
    telegramRotationFailed: 'Telegram Token 轮换失败',
    telegramRotationOpening: '本机隐藏输入框正在打开',
    telegramTokenNotice: 'Token 不经过浏览器。点击后在本机隐藏输入框粘贴 BotFather 最新整条回复，系统会自动校验 Bot 身份、原子更新、只重启 notifier 并验证加载结果。',
    telegramWaitingForInput: '等待本机输入…',
    telegramRotateToken: '更换 Token（本机安全）',
    telegramChatId: 'Chat ID',
    telegramSaveChatId: '保存 Chat ID',
    tradierCredentials: 'Tradier API 凭证',
    tradierRestartNotice: '写入 .env 已生效，但已运行的交易进程使用启动时缓存的环境变量。新凭证用于实际下单前，需要重启以下服务：',
    tradierRestartServices: 'quant-r10-execution-worker、quant-r10-order-status-watcher、quant-r10-broker-oracle、quant-r10-market-clock。',
    tradierProductionKey: '生产 API Key',
    tradierLiveAccount: '实盘账户 ID',
    tradierSandboxAccount: '沙盒账户 ID',
    mode: '模式',
    liveModeWarning: '⚠ live 使用真实资金账户。Paper 使用沙盒 REST（无流式数据，延迟 15 分钟）。',
  },
  errors: {
    crash: '发生错误',
    retry: '重试',
  },
  components: {
    // 2026-06-22 build chain repair commit #3 — flat sections referenced
    // via t.components.{X} (NOT via t.components.tradier.{X} which is the
    // sub-namespace for the tradier sub-component). Keys are read directly
    // from useI18n().t.components.* in MarketSessionBadge / MarketTape /
    // PageShell / SystemResourcesBar / Broker.
    marketSession: {
      label: '交易时段',
      states: {
        OPEN: '开盘中',
        PRE_MARKET: '盘前',
        AFTER_HOURS: '盘后',
        CLOSED: '已收盘',
        HALTED: '暂停',
        UNKNOWN: '未知',
      },
    },
    marketTape: {
      label: '行情磁带',
      live: '实时',
      stale: '陈旧',
      fear: '恐慌',
      closed: '已收盘',
    },
    pageShell: {
      loading: '加载中…',
      reconnect: '重新连接',
      lastUpdate: '更新于',
    },
    systemResources: {
      title: '系统资源',
      cpu: 'CPU',
      cpuTitle: 'CPU {0}% / {1} 核',
      mem: '内存',
      memTitle: '内存 {0} GB / {1} GB',
      load: '负载',
      loadTitle: '1 分钟负载: {0}',
      pipeline: '流水线',
      pipelineTitle: '流水线进程 CPU 使用',
    },
    broker: {
      // Read by Broker.tsx:196 `const br = t.components?.broker ?? {};`
      // All 16 keys are defensive — the page renders empty strings if any
      // is missing, but the TS errors block tsc.
      freeze: '冻结',
      freezeReason: '冻结原因',
      frozenDefault: '已冻结',
      goLive: '查看实盘',
      goSandbox: '查看沙盒',
      viewOnlyHint: '仅切换查看——只改看哪个账户的数据，不改变系统实际交易的账户（那要走守卫的 /api/r10/access 流程）。',
      viewingBanner: '👁 正在查看 {0} · 系统实际交易在 {1}',
      loadingRuntime: '正在加载运行时状态…',
      pageBootHeadline: '启动中',
      pageFrozenHeadline: '已冻结',
      pageLiveHeadline: '实盘',
      pageSandboxHeadline: '沙盒',
      paneLive: '实盘',
      panePaper: '模拟',
      showOtherAccount: '加载另一个账户',
      hideOtherAccount: '隐藏另一个账户',
      runtimeState: '运行时状态: {0}',
      transitionAccepted: '切换已接受: {0}',
      transitionFailed: '切换失败: {0}',
      unfreeze: '解冻',
    },
    metrics: {
      title: '系统指标',
      noData: '无指标数据',
      eventsPerSec: '事件/秒',
      quotes: '行情数',
      orders: '订单数',
      fillRate: '成交率',
      rejects: '拒绝数',
      quoteDelay: '行情延迟',
    },
    scoreRanking: {
      title: '评分排名',
      noSignals: '无信号 — 注入行情数据',
      noBuySignals: '无买入信号 — 注入行情数据',
      noSellSignals: '独立卖出评分排名尚未接入',
      buyTitle: '买入评分排名',
      sellTitle: '卖出评分排名',
      sellNotIntegrated: '独立卖出评分排名尚未接入',
      buyThreshold: 'BUY 阈值',
      sellThreshold: '卖出阈值',
      symbol: '标的',
      score: '评分',
      signal: '信号',
      topN: '前 10',
      distHigh: '距52周高',
      changePct: '涨跌',
      freshnessRefreshing: '刷新中',
      freshnessUpdated: '已更新',
      freshnessWarming: '首次计算中',
      freshnessAge: '数据年龄',
      freshnessCompute: '计算耗时',
      freshnessStale: '数据偏旧',
    },
    governance: {
      title: 'V12 认证',
      g1: '事件层',
      g2: '规范Bar',
      g3: '评分层',
      g4: '执行层',
      architecture: '架构',
      model: '模型',
    },
    databases: {
      title: '数据库',
      questdb: 'QuestDB',
      duckdb: 'DuckDB',
      status: '状态',
      connected: '已连接',
      disconnected: '已断开',
      error: '错误',
      tables: '表',
      host: '主机',
      ilpPort: 'ILP 端口',
      httpPort: 'HTTP 端口',
      noTables: '无表',
      refresh: '刷新',
      start: '启动',
      stop: '停止',
      // 2026-06-12 multi-DB extension
      kind: '类型',
      version: '版本',
      uptime: '运行时长',
      path: '路径',
      rows: '行数',
      size: '大小',
      file: '文件',
      namespace: '命名空间',
      keys: '键',
      memory: '内存',
      clients: '客户端',
      retired: '已停用',
      empty: '空',
      processes: '进程',
      summary: '摘要',
      tablesCount: '表数量',
      pid: '进程号',
      sampledKeys: '采样键',
      noFiles: '无文件',
      noDetail: '无详情',
      loading: '加载中…',
      symbols: '标的',
      timeColumn: '时间列',
      oldest: '最早时间',
      newest: '最新时间',
      freshness: '新鲜度',
      quality: '质量',
      freshToday: '今日',
      freshRecent: '近期',
      freshStale: '滞后',
      freshOld: '过旧',
      backends: '数据系统',
      topics: '主题',
      topic: '主题',
      partitions: '分区',
      messages: '消息数',
      brokers: '节点数',
      mode: '运行模式',
      replicationFactor: '最小副本数',
      minimumIsr: '最小 ISR',
      offsetRange: 'Offset 范围',
      shadowHealth: '影子链路健康',
      backlog: '待发布',
      claimed: '已领取',
      poison: '异常事件',
      oldestBacklog: '最老积压',
      healthTtl: '健康 TTL',
      lag: '消费滞后',
      consumerGroup: '审计消费组',
      commitOn: '提交开启',
      commitOff: '提交关闭',
      runtimeAuthority: '运行权威',
      executionAuthority: '执行权威',
      tradingGate: '交易许可门禁',
      noTopics: '无业务主题',
      kafkaBoundary: 'Kafka 是事件传输骨干，不是交易事实数据库。',
      kafkaBoundaryDetail: 'PostgreSQL 仍是执行持久事实权威；Kafka 可用性不会进入现有交易运行门禁。',
    },
    tradier: {
      title: 'Tradier 券商',
      refresh: '刷新',
      cachedSnapshotRefreshing: '正在显示本地券商快照并后台刷新',
      marketOpen: '盘中',
      marketClosed: '休市',
      accountId: '账户',
      // Overview
      totalEquity: '总权益',
      cash: '现金',
      buyingPower: '购买力',
      unrealizedPnl: '未实现盈亏',
      portfolioValue: '投资组合价值',
      dayTrades: '日内交易次数',
      accountStatus: '状态',
      // Positions
      positions: '持仓',
      noPositions: '无持仓',
      qty: '数量',
      costBasis: '成本',
      mktValue: '市值',
      currentPrice: '当前价',
      unrealPnl: '未实现盈亏',
      pnlPct: '盈亏比例',
      dateAcquired: '建仓日期',
      buyAction: '买入',
      sellAction: '卖出',
      buyToOpen: '买入开仓',
      sellToClose: '卖出平仓',
      buyToClose: '买入平仓',
      sellToOpen: '卖出开仓',
      sellShort: '卖空',
      buyToCover: '买回平空',
      orderStatus: {
        open: '待成交',
        pending: '待提交',
        submitted: '已提交',
        partiallyFilled: '部分成交',
        filled: '已成交',
        rejected: '已拒绝',
        cancelled: '已取消',
        expired: '已过期',
        pendingCancel: '取消中',
      },
      prefillOrder: '已从持仓生成订单票据',
      // Orders
      orders: '订单',
      noOrders: '无订单',
      placeOrder: '下单',
      cancelOrder: '取消',
      orderId: '订单ID',
      side: '方向',
      type: '类型',
      duration: '有效期',
      price: '价格',
      filledQty: '成交量',
      avgPrice: '均价',
      created: '创建时间',
      filledTime: '成交时间',
      acquired: '建仓时间',
      market: '市价',
      limit: '限价',
      stopLimit: '止损限价',
      durationPre: '盘前',
      durationPost: '盘后',
      liveManualBlocked: '实盘禁止从此页直接下单，须走受保护的 R10 执行通道。手动买卖请用沙盒账户。',
      sessionClosedHint: '当前不在常规交易时段。市价+当日有效会被 Tradier 拒绝，请改用限价并选择 GTC / 盘前 / 盘后。',
      day: '当日有效',
      gtc: '永久有效',
      shortTerm: '短期',
      longTerm: '长期',
      // Quotes
      quotes: '报价',
      symbol: '标的',
      search: '标的搜索',
      searchPlaceholder: '按名称或代码搜索...',
      noResults: '无结果',
      bid: '买价',
      ask: '卖价',
      last: '最新价',
      size: '数量',
      halted: '停牌',
      // Watchlists
      watchlists: '自选列表',
      noWatchlists: '无自选列表',
      // P&L
      gainLoss: '已实现盈亏',
      noGainLoss: '无已实现盈亏记录',
      openDate: '开仓日期',
      closeDate: '平仓日期',
      proceeds: '收益',
      cost: '成本',
      realizedPnl: '已实现盈亏',
      term: '期限',
      pnlCalendar: '盈亏日历',
      // History
      history: '历史OHLCV',
      start: '开始日期',
      end: '结束日期',
      interval: '周期',
      daily: '日',
      weekly: '周',
      monthly: '月',
      open: '开盘价',
      high: '最高价',
      low: '最低价',
      close: '收盘价',
      volume: '成交量',
      // Options
      options: '期权',
      optionChain: '期权链',
      optionSymbol: '标的代码',
      noOptions: '无期权数据，请输入标的代码后按刷新',
      expiration: '到期日',
      calls: '看涨期权',
      puts: '看跌期权',
      strike: '行权价',
      optBid: '买价',
      optAsk: '卖价',
      optLast: '最新价',
      iv: '隐含波动率',
      delta: 'Delta',
      gamma: 'Gamma',
      theta: 'Theta',
      vega: 'Vega',
      optVol: '成交量',
      openInterest: '未平仓量',
      showGreeks: '显示希腊值',
      // Account modes
      paperMode: '模拟账户',
      liveMode: '真实账户',
      // Profile
      profile: '账户信息',
      name: '姓名',
      broker: '券商',
      accountType: '账户类型',
      // Account History
      accountHistory: '账户历史',
      noAccountHistory: '无账户历史',
      date: '日期',
      amount: '金额',
      balance: '余额',
      description: '描述',
      status: '状态',
      entryType: '类型',
      action: '操作',
      stop: '止损',
      // Weekdays
      sun: '日',
      mon: '一',
      tue: '二',
      wed: '三',
      thu: '四',
      fri: '五',
      sat: '六',
      exchange: '交易所',
      // Calendar
      calendar: '市场日历',
      nextStateChange: '下次状态变更',
      calOpen: '开市',
      calHalf: '半天',
      calClosed: '休市',
      nextMonth: '下月',
      prevMonth: '上月',
      // Watchlist extras
      emptyWatchlist: '空列表',
      symbolCount: '{0} 个标的',
      selectWatchlist: '选择自选列表',
      // Options extras
      allStrikes: '全部',
      // ETB
      etbCount: '显示 {0} / {1}',
      filterPlaceholder: '筛选标的...',
      noEtbLoaded: '未加载',
      // P&L extras
      closedPositions: '已平仓',
      totalPnl: '总盈亏',
      noClosedPositions: '{0}年无已平仓记录',
      // Misc
      add: '添加',
      create: '创建',
      delete: '删除',
      load: '加载',
      // Errors
      apiError: 'API错误',
      connectionFailed: '连接失败',
      rateLimited: '请求受限',
      noApiKey: '未配置API密钥',
      // 2026-06-22 build chain repair commit #3 — keys referenced via
      // `tt = t.components.tradier` in PositionGroupsTable.tsx and
      // MarketSessionBadge.tsx / MarketTape.tsx. The MarketSessionBadge
      // component reads marketSession as { label, states: {OPEN, ...} }.
      marketSession: {
        label: '交易时段',
        states: {
          OPEN: '开盘中',
          PRE_MARKET: '盘前',
          AFTER_HOURS: '盘后',
          CLOSED: '已收盘',
          HALTED: '暂停',
          UNKNOWN: '未知',
        },
      },
      marketTape: {
        label: '行情磁带',
        live: '实时',
        stale: '陈旧',
        fear: '恐慌',
        closed: '已收盘',
      },
      positionGroups: '持仓分组',
      positionGroupsEmpty: '暂无持仓分组',
      positionGroupName: '名称',
      positionGroupCount: '持仓数',
      positionGroupCreate: '创建',
      positionGroupNamePlaceholder: '分组名称…',
      positionGroupActions: '操作',
    },
  },
  login: {
    brand: '量化交易终端',
    subtitle: '黑紫操作员控制台',
    signIn: '登入',
    title: '仪表盘登入认证',
    username: '用户名',
    password: '密码',
    submit: '登入',
    submitting: '登入中…',
    system: '系统',
    operational: '运行中',
    unconfigured: '未配置',
    probing: '探测中',
    architecture: '架构',
    model: '模型',
    uptime: '运行时长',
    governance: '治理门控',
    market: '大盘',
    live: '实时',
    offline: '离线',
    stale: '陈旧',
    closed: '闭市',
    changePasswordHint: '登入后请在',
    inSettings: '设置',
    afterLogin: '页面修改密码',
    rightsInternal: '内部使用',
    fear: '恐慌',
  },
};
